import { useState } from "react";
import { fmtMXNFull, fmtDate } from "@/lib/api";
import IncontiLogo from "@/components/IncontiLogo";
import SignaturePad from "@/components/SignaturePad";
import { PenLine } from "lucide-react";

export default function MachineryReportSheet({ report, submitSignature }) {
  const [padRole, setPadRole] = useState(null);
  const [auxName, setAuxName] = useState("");
  const [signing, setSigning] = useState(false);

  const confirm = async (dataUrl) => {
    setSigning(true);
    try {
      await submitSignature(padRole, dataUrl, padRole === "auxiliar" ? auxName.trim() : null);
      setPadRole(null);
    } finally {
      setSigning(false);
    }
  };

  const folio = `MAQ-${report.id.slice(0, 8).toUpperCase()}`;
  const isDia = report.rental_type === "dia";
  const isMes = report.rental_type === "mes";
  const isViaje = report.rental_type === "viaje";
  const isHorometro = report.rental_type === "horometro";

  return (
    <>
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .receipt { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; }
        }
      `}</style>
      <div className="receipt mx-auto bg-white text-slate-900 max-w-[820px] shadow-2xl" style={{ minHeight: "1000px" }}>
        <div className="px-10 pt-10 pb-6 border-b-4" style={{ borderColor: "#3F2A91" }}>
          <div className="flex items-start justify-between">
            <div>
              <IncontiLogo size={56} />
              <div className="mt-3 text-xs text-slate-500 leading-snug">
                CORPORATION INCONTI · administracion@inconti.net
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">// FOLIO</div>
              <div className="font-mono text-lg font-bold text-slate-900">{folio}</div>
              <div className="mt-2 text-[11px] uppercase tracking-widest text-slate-400 font-mono">// EMITIDO</div>
              <div className="font-mono text-xs text-slate-700">{fmtDate(report.created_at)}</div>
            </div>
          </div>
        </div>

        <div className="px-10 py-6 text-center">
          <h1 className="text-3xl font-display font-black tracking-widest" style={{ color: "#3F2A91" }}>
            REPORTE DE RENTA DE MAQUINARIA
          </h1>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
            {isViaje ? "Cobro por viaje (volteo / acarreo)" : isMes ? "Renta mensual fija" : isDia ? "Cobro por día" : isHorometro ? "Cobro por horómetro" : "Cobro por hora"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 px-10 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">MÁQUINA / EQUIPO</div>
            <div className="font-bold text-lg text-slate-900">{report.machine_name}</div>
            <div className="text-sm text-slate-600">Proveedor: {report.provider_name}</div>
            <div className="text-xs text-slate-500 font-mono mt-1">Tarifa: {fmtMXNFull(report.rate)} {isViaje ? "/ viaje" : isMes ? "/ mes" : isDia ? "/ día" : "/ hora"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">PERIODO</div>
            <div className="font-bold text-base text-slate-900 font-mono">{fmtDate(report.period_start)} → {fmtDate(report.period_end)}</div>
            <div className="mt-2 px-2 py-1 inline-block rounded text-xs font-semibold" style={{ background: "#3F2A9120", color: "#3F2A91" }}>
              PROYECTO: {report.project_name}
            </div>
            <div className="text-xs text-slate-600 mt-2"><b>Operador:</b> {report.operator_name}</div>
          </div>
        </div>

        <div className="px-10 mb-6">
          <div className="border rounded" style={{ borderColor: "#cbd5e1" }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-widest font-mono text-white" style={{ background: "#3F2A91" }}>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  {isHorometro && <th className="px-3 py-2 text-right">Horómetro</th>}
                  <th className="px-3 py-2 text-right">{isViaje ? "Viajes" : (isDia || isMes) ? "Días" : "Horas"}</th>
                  <th className="px-3 py-2 text-right">Importe</th>
                  <th className="px-3 py-2 text-left">Notas</th>
                </tr>
              </thead>
              <tbody>
                {report.logs.map((l, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                    <td className="px-3 py-2 font-mono">{fmtDate(l.date)}</td>
                    {isHorometro && <td className="px-3 py-2 text-right font-mono text-slate-600">{l.hourometer_start != null ? `${l.hourometer_start} → ${l.hourometer_end}` : "—"}</td>}
                    <td className="px-3 py-2 text-right font-mono">{isViaje ? (l.trips ?? "—") : (isDia || isMes) ? (l.days ?? "—") : (l.hours ?? "—")}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{isMes ? "—" : fmtMXNFull(l.amount)}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{[l.rate_label, l.material, l.notes].filter(Boolean).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-bold" style={{ borderColor: "#3F2A91", background: "#f8fafc" }}>
                  <td className="px-3 py-2 uppercase tracking-widest text-xs">Total{isMes ? " (renta mensual)" : ""}</td>
                  {isHorometro && <td />}
                  <td className="px-3 py-2 text-right font-mono">{isViaje ? `${report.total_trips} viajes` : (isDia || isMes) ? `${report.total_days} días` : `${report.total_hours} hrs`}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums" style={{ color: "#3F2A91" }}>{fmtMXNFull(report.total_amount)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Firmas */}
        <div className="grid grid-cols-3 gap-8 px-10 mb-12 mt-16">
          <div className="text-center">
            <div className="border-t-2 pt-2 mt-12" style={{ borderColor: "#0f172a" }}>
              <div className="text-xs uppercase tracking-widest font-bold">ELABORÓ</div>
              <div className="text-sm mt-1 font-semibold">{report.created_by_name || "________________________"}</div>
              <div className="text-[10px] text-slate-500 mt-1">CORPORATION INCONTI</div>
            </div>
          </div>

          <div className="text-center">
            <div className="border-t-2 pt-2 mt-12" style={{ borderColor: "#0f172a", position: "relative" }}>
              {report.signature && (
                <img src={report.signature} alt="Firma operador" style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", height: 70, maxWidth: "90%", objectFit: "contain" }} />
              )}
              <div className="text-xs uppercase tracking-widest font-bold">OPERADOR DEL EQUIPO</div>
              <div className="text-sm mt-1 font-semibold">{report.signed_name || report.operator_name}</div>
              <div className="text-[10px] text-slate-500 mt-1">
                {report.signature ? `FIRMADO DIGITALMENTE · ${fmtDate(report.signed_at)}` : "FIRMA DE CONFORMIDAD DE PAGO"}
              </div>
              {!report.signature && (
                <button
                  type="button"
                  data-testid="sign-machinery-report-btn"
                  onClick={() => setPadRole("operador")}
                  className="no-print mt-3 px-4 py-2 rounded text-xs uppercase tracking-widest font-bold text-white inline-flex items-center gap-2"
                  style={{ background: "#3F2A91" }}
                >
                  <PenLine className="w-3.5 h-3.5" /> Firmar
                </button>
              )}
            </div>
          </div>

          <div className="text-center">
            <div className="border-t-2 pt-2 mt-12" style={{ borderColor: "#0f172a", position: "relative" }}>
              {report.aux_signature && (
                <img src={report.aux_signature} alt="Firma auxiliar" style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", height: 70, maxWidth: "90%", objectFit: "contain" }} />
              )}
              <div className="text-xs uppercase tracking-widest font-bold">AUXILIAR DE CAMPO</div>
              <div className="text-sm mt-1 font-semibold">{report.aux_signed_name || "________________________"}</div>
              <div className="text-[10px] text-slate-500 mt-1">
                {report.aux_signature ? `FIRMADO DIGITALMENTE · ${fmtDate(report.aux_signed_at)}` : "FIRMA DE CONFORMIDAD"}
              </div>
              {!report.aux_signature && (
                <div className="no-print mt-3 space-y-1.5">
                  <input
                    data-testid="aux-name-input"
                    placeholder="Nombre del auxiliar"
                    value={auxName}
                    onChange={(e) => setAuxName(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border rounded text-slate-900"
                    style={{ borderColor: "#cbd5e1" }}
                  />
                  <button
                    type="button"
                    data-testid="sign-aux-report-btn"
                    disabled={!auxName.trim()}
                    onClick={() => setPadRole("auxiliar")}
                    className="px-4 py-2 rounded text-xs uppercase tracking-widest font-bold text-white inline-flex items-center gap-2 disabled:opacity-40"
                    style={{ background: "#3F2A91" }}
                  >
                    <PenLine className="w-3.5 h-3.5" /> Firmar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-10 py-4 text-center text-[10px] text-slate-400 border-t" style={{ borderColor: "#e5e7eb" }}>
          Documento generado por Corporation Inconti ERP · Folio {folio} · {fmtDate(report.created_at)}
        </div>
      </div>

      {padRole && (
        <SignaturePad
          title={padRole === "auxiliar" ? `${auxName} · Auxiliar de campo` : `${report.operator_name} · ${report.machine_name}`}
          onConfirm={confirm}
          onClose={() => setPadRole(null)}
          saving={signing}
        />
      )}
    </>
  );
}
